import React from "react";
import Box from "@mui/material/Box";
import formatDate from "date-fns/format";
import Button from "@mui/material/Button";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import getMonth from "date-fns/getMonth";
import getYear from "date-fns/getYear";
import MonthYearSelectMenu from "@app.automotus.io/components/MonthYearSelectMenu/MonthYearSelectMenu";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridToolbarFilterButton,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { Theme } from "@mui/material/styles";
import CustomExportButton from "@app.automotus.io/components/CustomExportButton";
import Stack from "@mui/material/Stack";
import { ColumnsToolbarButton, ResetLayoutButton } from "../../DataTableContainer";

/**
 * Exposes interactions to a payee user to filter tabular transaction data.
 * When display mode is "month", allows the user to select the month of data that will be displayed.
 * When display mode is "day", allows the user to view the currently selected day.
 */
export const PayeeTableToolbar: React.FC<PayeeTableToolbarProps> = ({
  selectedDate,
  onChangeSelectedDate = () => undefined,
  maxDate,
  exportFileName,
  columns,
  fetchTotalCount,
  fetchDataBatch,
  batchSize,
  dateFormat = "MMMM",
  showDatePicker = true,
  defaultVisibilityModel,
  isIconOnly = false,
  resetLayout,
}) => {
  const [dropdownAnchorEl, setDropdownAnchorEl] = React.useState<null | HTMLElement>(null);
  const dropdownOpen = Boolean(dropdownAnchorEl);
  const apiRef = useGridApiContext();

  const handleClickDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setDropdownAnchorEl(event.currentTarget);
  };

  const handleCloseDropdown = () => {
    setDropdownAnchorEl(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: showDatePicker ? "space-between" : "flex-start",
        alignItems: "center",
        height: 52,
        px: 1.25,
        bgcolor: (theme: Theme) => theme.palette.background.paper,
      }}
    >
      <Stack direction="row" justifyContent="flex-start" alignItems="center" height="100%" spacing={1}>
        <GridToolbarFilterButton
          componentsProps={{
            button: {
              sx: {
                fontSize: "16px",
                fontWeight: 400,
                letterSpacing: 0.15,
                textTransform: "none",
                ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
              },
            },
          }}
        />
        <ColumnsToolbarButton apiRef={apiRef} defaultVisibilityModel={defaultVisibilityModel} />
        <CustomExportButton
          getTotalCount={fetchTotalCount}
          fetchDataBatch={fetchDataBatch}
          fileName={exportFileName}
          columns={columns}
          aria-label="Export"
          buttonLabel={isIconOnly ? "" : "Export"}
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            letterSpacing: 0.15,
            textTransform: "none",
            ...(isIconOnly ? { minWidth: "auto", px: 1, "& .MuiButton-startIcon": { mr: 0, ml: 0 } } : {}),
          }}
          batchSize={batchSize}
        />
        <ResetLayoutButton onReset={resetLayout} isIconOnly={isIconOnly} />
      </Stack>
      {showDatePicker && (
        <>
          <Button
            id={"table-tools-month-select-dropdown-button"}
            aria-controls={dropdownOpen ? "table-tools-month-select-menu" : undefined}
            aria-haspopup={"true"}
            aria-expanded={dropdownOpen ? "true" : undefined}
            variant={"text"}
            size={"large"}
            endIcon={<ArrowDropDownIcon />}
            sx={{ textTransform: "none" }}
            onClick={handleClickDropdown}
          >
            {formatDate(selectedDate, dateFormat)}
          </Button>
          <MonthYearSelectMenu
            month={getMonth(selectedDate)}
            year={getYear(selectedDate)}
            maxDate={maxDate}
            open={dropdownOpen}
            anchorEl={dropdownAnchorEl}
            onClose={handleCloseDropdown}
            onChange={onChangeSelectedDate}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          />
        </>
      )}
    </Box>
  );
};

/** Props passed to initialize a {@link PayeeTableToolbar} component instance */
export interface PayeeTableToolbarProps {
  /** Currently selected date for display, or the beginning of the month when displayMode is month */
  selectedDate: Date;
  /** Handler fired when selected date is changed */
  onChangeSelectedDate?: (d: Date) => void;
  /** maximum allowed date for selection */
  maxDate: Date;
  /** Name of the exported file. If left undefined, will simply use the page title */
  exportFileName?: string;
  /** Columns to export */
  columns: GridColDef[];
  /** Fetch total count of rows */
  fetchTotalCount: () => Promise<number>;
  /** Fetch a batch of rows */
  fetchDataBatch: (offset: number, limit: number) => Promise<unknown[]>;
  batchSize: number;
  /** date-fns format string for the month picker button label. Defaults to "MMMM". */
  dateFormat?: string;
  /** Whether to show the month/year date picker. Defaults to true. */
  showDatePicker?: boolean;
  /** The table's default column visibility — see useHiddenColumnsCount's docs on why this must stay a stable, code-defined constant. */
  defaultVisibilityModel: GridColumnVisibilityModel;
  /** Below 700px, collapses the export button to icon-only. The filter button's label is controlled separately, at the parent DataGridPro's `localeText` prop (MUI's built-in filter button reads its label from grid locale text, not from a prop here). Defaults to false. */
  isIconOnly?: boolean;
  /** Clears this table's saved layout and restores its defaults — see DataTableContainerChildrenParams.resetLayout. */
  resetLayout: () => void;
}

export type PayeeTableDisplayMode = "day" | "month";

export default PayeeTableToolbar;
